// useServiceabilityCheck.js
// ----------------------------------------------------------------------
// Reusable hook for the customer booking flow's pickup-serviceability
// gate. Debounces the POST /api/serviceability/check call by 500ms so
// every keystroke in the address field doesn't slam the backend.
//
// Returns:
//   { loading, serviceable, matchedHubName, matchedHubCity, distanceKm,
//     reason, nearestHubName, deliveryValid, deliveryPincode, refresh }
//
// Usage:
//   const sv = useServiceabilityCheck({ pickup_lat, pickup_lng,
//                                       pickup_pincode, delivery_pincode });
//   if (!sv.serviceable) <…red banner…>
//
// All booking screens (web + future mobile) should consume this hook
// so the gate behaviour stays identical.
import { useEffect, useRef, useState, useCallback } from "react";
import api from "../services/api";

const DEBOUNCE_MS = 500;

export default function useServiceabilityCheck({
  pickup_lat,
  pickup_lng,
  pickup_pincode,
  delivery_pincode,
}) {
  const [state, setState] = useState({
    loading: false,
    serviceable: null,           // null = not yet checked, false/true after
    matchedHubName: null,
    matchedHubCity: null,
    matchedHubId: null,
    distanceKm: null,
    reason: null,
    nearestHubName: null,
    nearestHubCity: null,
    deliveryValid: null,
    deliveryPincode: null,
  });

  const timerRef = useRef(null);
  const reqIdRef = useRef(0);

  const run = useCallback(async () => {
    const myReq = ++reqIdRef.current;
    // If we don't have a pickup point AND no pincode, don't ping.
    if (
      (pickup_lat == null || pickup_lng == null) &&
      !pickup_pincode &&
      !delivery_pincode
    ) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.post("/serviceability/check", {
        pickup_lat: pickup_lat ?? null,
        pickup_lng: pickup_lng ?? null,
        pickup_pincode: pickup_pincode || null,
        delivery_pincode: delivery_pincode || null,
      });
      // Bail if a newer request superseded this one.
      if (myReq !== reqIdRef.current) return;
      const p = data?.pickup || {};
      const d = data?.delivery || {};
      setState({
        loading: false,
        serviceable: !!p.serviceable,
        matchedHubName: p.matched_hub_name || null,
        matchedHubCity: p.matched_hub_city || null,
        matchedHubId: p.matched_hub_id || null,
        distanceKm: p.distance_km ?? null,
        reason: p.reason || null,
        nearestHubName: p.nearest_hub_name || p.matched_hub_name || null,
        nearestHubCity: p.nearest_hub_city || p.matched_hub_city || null,
        deliveryValid: d.valid,
        deliveryPincode: d.pincode,
      });
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      // Network / 5xx — don't block the user, just leave serviceable=null.
      // The backend booking endpoint will be the ultimate gate.
      setState((s) => ({ ...s, loading: false, serviceable: null, reason: "check-failed" }));
    }
  }, [pickup_lat, pickup_lng, pickup_pincode, delivery_pincode]);

  // Debounce: any input change resets the 500ms timer.
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(run, DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [run]);

  return { ...state, refresh: run };
}
